import { useState } from 'react'
import {
  Button,
  Label,
  RadioGroup, RadioGroupItem,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  Textarea,
  Alert, AlertDescription,
} from '@creanizer/ui'
import { Image, AlertCircle } from 'lucide-react'
import type { SavedImagePrompt, ImageAspectRatio, ImageSize, ImageGenerateMode, PromptInputMethod } from '@creanizer/types'
import { MAX_REFERENCE_IMAGES } from '@creanizer/types'
import { apiClient } from '../../../../services/api'
import { useSettingsStore } from '../../../../stores/settingsStore'
import { ImageGenerateResult } from './ImageGenerateResult'
import { ImageGenerateSettings } from './ImageGenerateSettings'

interface ImageGeneratePanelProps {
  savedPrompts: SavedImagePrompt[]
  currentPrompt?: { positive: string; negative: string }
  onRequestGenerate?: () => void
}

export function ImageGeneratePanel({
  savedPrompts,
  currentPrompt,
  onRequestGenerate,
}: ImageGeneratePanelProps) {
  const { isApiKeySet } = useSettingsStore()

  // プロンプト入力方法
  const [promptInputMethod, setPromptInputMethod] = useState<PromptInputMethod>('generator')
  const [selectedSavedId, setSelectedSavedId] = useState<string>('')
  const [directPrompt, setDirectPrompt] = useState('')
  const [directNegativePrompt, setDirectNegativePrompt] = useState('')

  // 生成モード
  const [mode, setMode] = useState<ImageGenerateMode>('text_to_image')
  const [referenceImages, setReferenceImages] = useState<string[]>([])
  const [referencePreviews, setReferencePreviews] = useState<string[]>([])

  // マスク関連
  const [maskEnabled, setMaskEnabled] = useState(false)
  const [maskImage, setMaskImage] = useState<string | null>(null)
  const [insertImage, setInsertImage] = useState<string | null>(null)

  // 画像設定
  const [aspectRatio, setAspectRatio] = useState<ImageAspectRatio>('9:16')
  const [imageSize, setImageSize] = useState<ImageSize>('2K')

  // 結果
  const [result, setResult] = useState<{ imageData: string; mimeType: string } | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // 使用するプロンプトを取得
  const getPrompt = (): { positive: string; negative: string } => {
    if (promptInputMethod === 'generator' && currentPrompt) {
      return currentPrompt
    }
    if (promptInputMethod === 'saved' && selectedSavedId) {
      const saved = savedPrompts.find(p => p.id === selectedSavedId)
      if (saved) {
        return { positive: saved.result.positivePrompt, negative: saved.result.negativePrompt }
      }
    }
    if (promptInputMethod === 'direct') {
      return { positive: directPrompt, negative: directNegativePrompt }
    }
    return { positive: '', negative: '' }
  }

  // 参考画像アップロード（複数対応）
  const handleReferenceUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    const remainingSlots = MAX_REFERENCE_IMAGES - referenceImages.length
    if (remainingSlots <= 0) {
      setError(`参照画像は最大${MAX_REFERENCE_IMAGES}枚までです`)
      return
    }

    const filesToProcess = Array.from(files).slice(0, remainingSlots)
    let loadedCount = 0
    const newImages: string[] = []
    const newPreviews: string[] = []

    for (const file of filesToProcess) {
      if (!file.type.startsWith('image/')) {
        continue
      }
      const reader = new FileReader()
      reader.onload = () => {
        const base64 = reader.result as string
        newImages.push(base64)
        newPreviews.push(base64)
        loadedCount++
        if (loadedCount === filesToProcess.length) {
          setReferenceImages(prev => [...prev, ...newImages])
          setReferencePreviews(prev => [...prev, ...newPreviews])
          setError(null)
        }
      }
      reader.onerror = () => setError('画像の読み込みに失敗しました')
      reader.readAsDataURL(file)
    }
    e.target.value = ''
  }

  // 参考画像を削除
  const handleReferenceRemove = (index: number) => {
    setReferenceImages(prev => prev.filter((_, i) => i !== index))
    setReferencePreviews(prev => prev.filter((_, i) => i !== index))
    // マスクと挿入画像をリセット
    setMaskEnabled(false)
    setMaskImage(null)
    setInsertImage(null)
  }

  // 全参考画像をクリア
  const handleReferenceClearAll = () => {
    setReferenceImages([])
    setReferencePreviews([])
    // マスクと挿入画像をリセット
    setMaskEnabled(false)
    setMaskImage(null)
    setInsertImage(null)
  }

  // 画像生成
  const handleGenerate = async () => {
    if (!isApiKeySet('gemini')) {
      setError('画像生成にはGemini APIキーが必要です')
      return
    }
    const prompt = getPrompt()
    if (!prompt.positive) {
      setError('プロンプトを入力してください')
      return
    }
    if (mode === 'image_edit' && referenceImages.length === 0) {
      setError('画像改変モードでは参考画像が必要です')
      return
    }

    setIsGenerating(true)
    setError(null)
    setResult(null)

    try {
      const response = await apiClient.generateImage({
        prompt: prompt.positive,
        negativePrompt: prompt.negative || undefined,
        mode,
        referenceImages: mode === 'image_edit' ? referenceImages : undefined,
        maskImage: mode === 'image_edit' && maskEnabled && maskImage ? maskImage : undefined,
        insertImage: mode === 'image_edit' && maskEnabled && insertImage ? insertImage : undefined,
        aspectRatio,
        imageSize,
      })
      setResult({ imageData: response.result.imageData, mimeType: response.result.mimeType })
    } catch (err) {
      setError(err instanceof Error ? err.message : '画像生成に失敗しました')
    } finally {
      setIsGenerating(false)
    }
  }

  const prompt = getPrompt()
  const hasPrompt = !!prompt.positive

  return (
    <div className="space-y-6">
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          画像生成には<strong>課金済みのAPIキー</strong>が必要です。無料枠では動作しません。
        </AlertDescription>
      </Alert>

      {/* プロンプト入力方法 */}
      <div className="space-y-3">
        <Label>プロンプト入力方法</Label>
        <RadioGroup value={promptInputMethod} onValueChange={(v) => setPromptInputMethod(v as PromptInputMethod)}>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="generator" id="pm-generator" />
            <Label htmlFor="pm-generator">ジェネレータで生成</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="saved" id="pm-saved" />
            <Label htmlFor="pm-saved">保存済みから選択</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="direct" id="pm-direct" />
            <Label htmlFor="pm-direct">直接入力</Label>
          </div>
        </RadioGroup>
      </div>

      {/* プロンプト入力エリア */}
      {promptInputMethod === 'generator' && (
        <div className="space-y-2">
          {currentPrompt?.positive ? (
            <div className="p-3 bg-green-50 border border-green-200 rounded">
              <Label className="text-green-800 mb-1 block">生成済みプロンプト</Label>
              <p className="text-sm text-green-700 line-clamp-3">{currentPrompt.positive}</p>
            </div>
          ) : (
            <div className="p-3 bg-gray-50 border border-gray-200 rounded">
              <p className="text-sm text-gray-600">
                「生成」タブでプロンプトを生成してください。
              </p>
              {onRequestGenerate && (
                <Button variant="outline" size="sm" className="mt-2" onClick={onRequestGenerate}>
                  生成タブへ移動
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {promptInputMethod === 'saved' && (
        <div className="space-y-2">
          <Label>保存済みプロンプトを選択</Label>
          <Select value={selectedSavedId} onValueChange={setSelectedSavedId}>
            <SelectTrigger><SelectValue placeholder="プロンプトを選択..." /></SelectTrigger>
            <SelectContent>
              {savedPrompts.map(p => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {promptInputMethod === 'direct' && (
        <div className="space-y-3">
          <div className="space-y-2">
            <Label>ポジティブプロンプト</Label>
            <Textarea value={directPrompt} onChange={(e) => setDirectPrompt(e.target.value)} rows={4} placeholder="生成したい画像の説明..." />
          </div>
          <div className="space-y-2">
            <Label>ネガティブプロンプト（オプション）</Label>
            <Textarea value={directNegativePrompt} onChange={(e) => setDirectNegativePrompt(e.target.value)} rows={2} placeholder="除外したい要素..." />
          </div>
        </div>
      )}

      <ImageGenerateSettings
        mode={mode}
        onModeChange={setMode}
        referencePreviews={referencePreviews}
        onReferenceUpload={handleReferenceUpload}
        onReferenceRemove={handleReferenceRemove}
        onReferenceClearAll={handleReferenceClearAll}
        maxImages={MAX_REFERENCE_IMAGES}
        aspectRatio={aspectRatio}
        onAspectRatioChange={setAspectRatio}
        imageSize={imageSize}
        onImageSizeChange={setImageSize}
        maskEnabled={maskEnabled}
        onMaskEnabledChange={setMaskEnabled}
        onMaskChange={setMaskImage}
        insertImage={insertImage}
        onInsertImageChange={setInsertImage}
      />

      <Button onClick={handleGenerate} disabled={isGenerating || !hasPrompt || (mode === 'image_edit' && referenceImages.length === 0)} className="w-full">
        <Image className="h-4 w-4 mr-2" />
        {isGenerating ? '生成中...' : '画像を生成'}
      </Button>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {result && <ImageGenerateResult imageData={result.imageData} mimeType={result.mimeType} />}
    </div>
  )
}
